import React, { useEffect, useState, useRef, useCallback } from 'react';
import CreateJKCTask from './components/CreateJKCTask';
import VehicleCard from './components/VehicleCard';
import CreateJKCTaskApi from '@/fetch/business/jkc/createTask';
import './index.scss';
import { HttpStatusCode } from '@/fetch/core/constant';
import { showErrorToast } from '@/utils/toast';
import { JkcHttpStatusCode } from '../utils/constant';
import NoTaskIcon from '@/assets/images/jkc/createTask/no-task.png';
import { TextOutline, RedoOutline } from 'antd-mobile-icons';
import { SpinLoading } from 'antd-mobile';
import { useNavigate } from 'react-router-dom';
import { sgmLog } from '@/utils';

const OrderListAndCreateTask = function () {
  const navigator = useNavigate();
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [inProgressOrders, setInProgressOrders] = useState<any[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [storeId, setStoreId] = useState<string | null>(null);
  const stationLocationRef = useRef({
    longitude: '',
    latitude: '',
    cityName: ''
  });
  const observer = useRef<IntersectionObserver>();
  const sentinelRef = useCallback(
    (node: any) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNum((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  useEffect(() => {
    getVehicleInfo();
  }, []);

  useEffect(() => {
    if (pageNum > 1 && storeId) {
      getInProgressOrders(storeId, pageNum);
    }
  }, [pageNum]);

  const getInProgressOrders = async (currentStoreId: string, page: number) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const res = await CreateJKCTaskApi.getInProgressOrder({
        storeId: currentStoreId,
        pageNum: page,
        pageSize: 20
      });
      if (res.code === JkcHttpStatusCode.Success && res.data) {
        sgmLog(
          'JCK_IN_PROGRESS_ORDER',
          'FETCH_DATA',
          JSON.stringify(res.data.list.map((o: any) => o.orderId))
        );
        setInProgressOrders((prev) => {
          const data = page === 1 ? res.data.list : [...prev, ...res.data.list];
          sgmLog('JCK_IN_PROGRESS_ORDER', 'PAGE_DATA', String(page));
          sgmLog(
            'JCK_IN_PROGRESS_ORDER',
            'DATA',
            JSON.stringify(data.map((o: any) => o.orderId))
          );
          return data;
        });
        setHasMore(res.data.hasNextPage ?? res.data.list.length > 0);
      } else {
        setHasMore(false);
      }
    } catch (error: any) {
      showErrorToast('加载订单失败');
      setHasMore(false);
      sgmLog('JCK_GET_IN_PROGRESS_ORDERS', 'FAIL', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getVehicleInfo = async (isRefresh = false) => {
    if (isLoading && !isRefresh) return;
    setIsLoading(true);

    if (isRefresh) {
      setPageNum(1);
    }

    try {
      const stationRes = await CreateJKCTaskApi.getStationInfo();
      if (stationRes.code === HttpStatusCode.Success && stationRes.data) {
        stationLocationRef.current.longitude = stationRes.data.longitude;
        stationLocationRef.current.latitude = stationRes.data.latitude;
        stationLocationRef.current.cityName = stationRes.data.cityName;
      } else {
        throw new Error('获取站点信息失败');
      }

      const res = await CreateJKCTaskApi.getVehicleInfo({
        siteLocation: stationLocationRef.current
      });
      if (res.code === JkcHttpStatusCode.Success && res.data) {
        const currentStoreId = res.data.storeId;
        const simplifiedVehicleInfo = {
          vehicleInfoList: res.data.serviceInfo
            ? res.data.serviceInfo?.serviceProviderProducts
            : [],
          serviceInfo: {
            serviceMode: res.data.serviceInfo?.serviceMode,
            serviceType: res.data.serviceInfo?.serviceType
          },
          storeId: currentStoreId
        };
        setVehicleInfo(simplifiedVehicleInfo as any);
        setStoreId(currentStoreId);

        if (currentStoreId) {
          getInProgressOrders(currentStoreId, 1);
        }
      } else {
        throw new Error('获取车辆信息失败');
      }
    } catch (error: any) {
      showErrorToast(error.message || '请求失败');
      sgmLog('JCK_GET_VEHICLE_INFO', 'FAIL', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    getVehicleInfo(true);
    sgmLog('JCK_REFRESH', 'REFRESH', 'true');
  };

  const goToHistoryOrdersPage = useCallback(() => {
    if (!storeId) {
      showErrorToast('暂未获取到storeId，请稍后重试！');
      return;
    }
    const searchParams = new URLSearchParams({ storeId: storeId });
    navigator(`/jkc/historyOrders?${searchParams.toString()}`);
  }, [storeId]);
  sgmLog(
    'JCK_IN_PROGRESS_ORDER',
    'RENDER',
    JSON.stringify(inProgressOrders.map((o) => o.orderId))
  );

  return (
    <div className="jkc-order-list-create-task">
      <div className="section-header">
        <div className="section-title">
          <div className="vertical-line"></div>
          <span>载货车辆</span>
        </div>
        <div className="other-btns">
          <div
            className="history-order"
            clstag="transport_workbench|clickHistoryOrder"
            onClick={goToHistoryOrdersPage}
          >
            <TextOutline />
            历史订单
          </div>
          {isLoading ? (
            <div>...加载中</div>
          ) : (
            <div
              className="refresh"
              onClick={handleRefresh}
              clstag="transport_workbench|clickRefresh"
            >
              <RedoOutline />
              刷新
            </div>
          )}
        </div>
      </div>
      {inProgressOrders.length > 0 && (
        <div className="in-progress-orders">
          {inProgressOrders.map((order: any) => (
            <VehicleCard key={order.orderId} order={order} />
          ))}
          {hasMore && <div ref={sentinelRef} className="sentinel" />}
        </div>
      )}

      {isLoading && pageNum > 1 && (
        <div className="loading-more">
          <SpinLoading color="primary" style={{ '--size': '24px' }} />
          <span>加载中...</span>
        </div>
      )}

      {vehicleInfo && (
        <CreateJKCTask
          vehicleInfo={vehicleInfo}
          siteLocation={stationLocationRef.current}
          cityName={stationLocationRef.current.cityName}
        />
      )}

      {!isLoading && inProgressOrders.length === 0 && !vehicleInfo && (
        <div className="empty-state-container">
          <img className="no-task-icon" src={NoTaskIcon} />
        </div>
      )}
    </div>
  );
};

export default OrderListAndCreateTask;
